import * as H from 'history'
import moment from 'moment'
import { Fragment, useState, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import agent from 'agent'
import {
  PanelHeader,
  PanelTitle,
  PanelDetailsGrid,
  Label,
  Value,
  PanelSection
} from 'components/attacks/details/AttackStyles'
import Stage from 'components/attacks/details/Stage'
import HaltConfirmation from 'components/attacks/HaltConfirmation'
import {
  getTargetType,
  getTargetingStrategy,
  getCommand,
  isK8sAttack,
  getAttackConfiguration,
  isK8sTargetType
} from 'components/attacks/utils/attack-configuration'
import { STAGES } from 'components/attacks/utils/attack-stages'
import Button from 'components/common/Button'
import Pill from 'components/common/Pill'
import Text from 'components/common/Text'
import duration from 'duration'
import useAuth from 'hooks/use-auth'
import { useGremlinQuery } from 'hooks/use-gremlin-query'
import theme from 'theme'
import { track } from 'track'
import { InfraAttack } from 'types/api/attacks/details/infra-attack'
import { SelectionType } from 'types/api/attacks/details/targeting'
import { PagedServiceDependenciesResponse } from 'types/api/services/dependencies/dependency'
import { Metadata } from 'types/metadata'
import { getFormattedAttackType, getArgDisplayData } from 'utils/metadata'

const RerunAttackButton: React.FC<{ rerunLinkTo: H.LocationDescriptor }> = ({
  rerunLinkTo
}) => {
  const history = useHistory()

  return (
    <Button
      data-test-rerun-attack
      data-track-action="rerun-attack"
      data-track-category="attack"
      data-track-on="click"
      onClick={() => {
        history.push(rerunLinkTo)
        track('RerunAttack')
      }}
    >
      Rerun
    </Button>
  )
}

const DetailPanel: React.FC<{
  attack: InfraAttack
  doFetch?: () => void
  isScenario?: boolean
  isService?: boolean
  metadata: Metadata
  rerunLinkTo?: H.LocationDescriptor
  showRerunButton?: boolean
}> = ({
  attack,
  doFetch,
  isScenario,
  isService,
  metadata,
  rerunLinkTo,
  showRerunButton
}) => {
  const { currentTeamId } = useAuth()
  const history = useHistory()
  const { search: queryParams = '' } = useLocation()
  const attackConfiguration = getAttackConfiguration(attack)

  const [isHaltConfirmationOpen, setIsHaltConfirmationOpen] = useState(false)
  const attackId = attack?.guid
  const command = getCommand(attackConfiguration)
  const targetingStrategy = getTargetingStrategy(attackConfiguration)
  const targetType = getTargetType(attackConfiguration)

  const serviceId = attackConfiguration.targets.map((t) => t.serviceId)[0]
  const targetedDependencies =
    command?.infraCommandArgs?.trafficImpactMapping?.dependencyIds

  const { data: dependencies } =
    useGremlinQuery<PagedServiceDependenciesResponse>(
      `/services/${serviceId}/dependencies?teamId=${currentTeamId}`,
      {},
      {
        enabled: serviceId && targetedDependencies?.length > 0,
        placeholderData: {} as PagedServiceDependenciesResponse
      }
    )

  useEffect(() => {
    const params = new URLSearchParams(queryParams.replace('?', ''))
    const userAction = params.get('userAction')
    if (userAction) {
      if (userAction === 'rerun') {
        history.replace(rerunLinkTo)
      } else if (userAction === 'halt') {
        setTimeout(() => {
          setIsHaltConfirmationOpen(true)
        }, 200)
      }
    }
  }, [queryParams, attack, history, rerunLinkTo])

  const attackError = () => {
    // K8s uses ERROR while normal tasks use Failed
    if (attack.stage !== 'Failed' && attack.stage != 'ERROR') {
      return ''
    }

    // TODO: remove once K8sAttacks migrate to display_error_message
    const message = attack.error || attack.display_error_message
    if (message) {
      return `: ${message}`
    }

    if (isK8sAttack(attack)) {
      return ': Could not resolve to any containers'
    }

    return ''
  }

  const showHalt = isService
    ? false
    : STAGES.IN_FLIGHT.some((stage) => stage === attack.stage) ||
      (isK8sAttack(attack) && attack.stage === 'RESOLVING')

  const showContainerNames =
    isK8sTargetType(targetingStrategy) &&
    targetingStrategy.hasOwnProperty('containerSelection') &&
    (targetingStrategy.containerSelection.selectionType === SelectionType.All ||
      (targetingStrategy.containerSelection.selectionType ===
        SelectionType.Exact &&
        targetingStrategy.containerSelection.containerNames?.length > 0))

  const displayableArgs = getArgDisplayData(
    attackConfiguration.command,
    metadata
  )
  return (
    <main data-test-detail-panel>
      <PanelSection css={{ display: 'flex' }}>
        <div css={{ flex: 1 }}>
          <PanelHeader>
            <PanelTitle>Configuration</PanelTitle>
          </PanelHeader>
          <PanelDetailsGrid>
            <Label>Type</Label>
            <Value>{getFormattedAttackType(command.infraCommandType)}</Value>
            <Label>Target Type</Label>
            <Value>{targetType}</Value>
            {displayableArgs
              .filter(
                ({ infraCommandArgKey }) => infraCommandArgKey !== 'length'
              )
              .map(({ displayName, value, short }) => (
                <Fragment key={short}>
                  <Label>{displayName}</Label>
                  <Value>{value.toString()}</Value>
                </Fragment>
              ))}
            {/* note: in this section of code below, `length` is a property on an object (the length of time argument) and not an array length */}
            {Boolean(command?.infraCommandArgs?.['length']) && (
              <Fragment>
                <Label>Length</Label>
                <Value>
                  {duration(command?.infraCommandArgs?.['length'])}

                  {/*display the length in seconds as well if it is greater than a minute*/}
                  {command?.infraCommandArgs?.['length'] >= 60 && (
                    <Text as="small" css={{ marginTop: theme.space[1] }}>{`${
                      command.infraCommandArgs.length
                    } ${
                      command.infraCommandArgs.length !== 1
                        ? 'seconds'
                        : 'second'
                    }`}</Text>
                  )}
                </Value>
              </Fragment>
            )}
            {command?.infraCommandArgs?.providers &&
              command.infraCommandArgs.providers.length > 0 && (
                <Fragment>
                  <Label css={{ lineHeight: '30px' }}>Providers</Label>
                  <Value>
                    {command.infraCommandArgs.providers.map((value) => {
                      return (
                        <Pill
                          key={`${attack.guid}-provider-${value}`}
                          showPointer
                          tooltip={value}
                        >
                          {value}
                        </Pill>
                      )
                    })}
                  </Value>
                </Fragment>
              )}
            {dependencies &&
              dependencies.items &&
              dependencies.items.length > 0 && (
                <Fragment>
                  <Label
                    css={{ lineHeight: '30px' }}
                    data-test-detail-label="dependencies"
                  >
                    Dependencies
                  </Label>
                  <Value>
                    {dependencies.items
                      .filter((dependency) =>
                        targetedDependencies?.includes(dependency.guid)
                      )
                      .map((value) => {
                        return (
                          <Pill
                            key={`${attack.guid}-dependencies-${value.guid}`}
                            showPointer
                            data-test-detail-value={value.guid}
                            tooltip={value.name}
                          >
                            {value.name}
                          </Pill>
                        )
                      })}
                  </Value>
                </Fragment>
              )}
            {isK8sTargetType(targetingStrategy) && showContainerNames && (
              <Fragment>
                <Label
                  css={{
                    lineHeight:
                      targetingStrategy.containerSelection.selectionType ===
                      'ALL'
                        ? 'initial'
                        : '30px'
                  }}
                >
                  Targeted Containers
                </Label>
                <Value>
                  {targetingStrategy.containerSelection.selectionType ===
                  'ALL' ? (
                    <Value>All</Value>
                  ) : (
                    targetingStrategy.containerSelection.containerNames.map(
                      (value) => {
                        return (
                          <Pill
                            key={`${attack.guid}-container-name-${value}`}
                            tooltip={value}
                          >
                            {value}
                          </Pill>
                        )
                      }
                    )
                  )}
                </Value>
              </Fragment>
            )}
          </PanelDetailsGrid>
        </div>
        <div css={{ flex: 1, marginLeft: theme.space[5] }}>
          <PanelHeader>
            <HaltConfirmation
              fetchAttack={doFetch}
              guid={attackId}
              open={isHaltConfirmationOpen}
              title="Halt attack"
              trackedEvent="SingleAttackHalted"
              trackingOptions={{ AttackId: attackId }} //scenarioId: id, runNumber: runNumber
              type="attack"
              onCancel={() => setIsHaltConfirmationOpen(false)}
              onConfirmRequest={({ reason }) => {
                if (isK8sAttack(attack) && attack.stage === 'RESOLVING') {
                  return agent.post(
                    `/kubernetes/attacks/${attack.uid}/halt?teamId=${currentTeamId}`
                  )
                }

                return agent.post(
                  `/attacks/${attackId}/halt?teamId=${currentTeamId}`,
                  { reason }
                )
              }}
            />

            <PanelTitle>Details</PanelTitle>
            {!isScenario && (
              <div css={{ marginLeft: 'auto' }}>
                {showHalt ? (
                  <Button
                    data-test-halt-attack
                    destructive
                    onClick={() => setIsHaltConfirmationOpen(true)}
                  >
                    Halt
                  </Button>
                ) : (
                  showRerunButton &&
                  rerunLinkTo && <RerunAttackButton rerunLinkTo={rerunLinkTo} />
                )}
              </div>
            )}
          </PanelHeader>
          <PanelDetailsGrid>
            <Label>Stage</Label>
            <Stage
              data-attack-details-stage
              error={attackError()}
              stage={attack.stage}
            />
            <Label>User</Label>
            <Value>{attack.create_user}</Value>
            <Label>Kind</Label>
            <Value>{attack.kind}</Value>
            <Label>Started</Label>
            <Value>
              {moment(new Date(attack.start_time)).format('M/D/YYYY h:mm a')}
            </Value>
            {attack.end_time && (
              <Fragment>
                <Label>Ended</Label>
                <Value>
                  {moment(new Date(attack.end_time)).format('M/D/YYYY h:mm a')}
                </Value>
              </Fragment>
            )}
          </PanelDetailsGrid>
        </div>
      </PanelSection>
    </main>
  )
}

export default DetailPanel
